/**
 * Willtec Collection JS
 * Handles desktop grid/list view toggling via data-layout attribute,
 * pure localStorage persistence (zero URL parameters, zero history mutation),
 * and mobile filter drawer open/close/accessibility.
 */
(() => {
  if (window.WilltecCollection) return;

  class WilltecCollection {
  static init() {
    this.initViewToggle();
    this.initMobileFilterDrawer();
    this.initFacetSearch();
  }

  static initViewToggle() {
    const gridBtn = document.querySelector('[data-layout-toggle="grid"], [data-view-toggle="grid"]');
    const listBtn = document.querySelector('[data-layout-toggle="list"], [data-view-toggle="list"]');
    const collectionContainer = document.querySelector('[data-collection-products], [data-product-results]');

    if (!collectionContainer) return;

    const STORAGE_KEY = 'willtec-collection-layout';
    const savedLayout = localStorage.getItem(STORAGE_KEY);
    const initialLayout = (savedLayout === 'list' || savedLayout === 'grid') ? savedLayout : 'grid';

    const setView = (mode) => {
      const targetMode = (mode === 'list' || mode === 'grid') ? mode : 'grid';
      collectionContainer.setAttribute('data-layout', targetMode);
      localStorage.setItem(STORAGE_KEY, targetMode);

      if (targetMode === 'list') {
        if (listBtn) {
          listBtn.classList.remove('text-[#818181]', 'border-[#DDDDDD]');
          listBtn.classList.add('text-black', 'border-black');
          listBtn.setAttribute('aria-pressed', 'true');
        }
        if (gridBtn) {
          gridBtn.classList.remove('text-black', 'border-black');
          gridBtn.classList.add('text-[#818181]', 'border-[#DDDDDD]');
          gridBtn.setAttribute('aria-pressed', 'false');
        }
      } else {
        if (gridBtn) {
          gridBtn.classList.remove('text-[#818181]', 'border-[#DDDDDD]');
          gridBtn.classList.add('text-black', 'border-black');
          gridBtn.setAttribute('aria-pressed', 'true');
        }
        if (listBtn) {
          listBtn.classList.remove('text-black', 'border-black');
          listBtn.classList.add('text-[#818181]', 'border-[#DDDDDD]');
          listBtn.setAttribute('aria-pressed', 'false');
        }
      }
    };

    // Apply layout without touching the URL
    setView(initialLayout);

    if (gridBtn) {
      gridBtn.onclick = (e) => {
        e.preventDefault();
        setView('grid');
      };
    }

    if (listBtn) {
      listBtn.onclick = (e) => {
        e.preventDefault();
        setView('list');
      };
    }
  }

  static initMobileFilterDrawer() {
    const trigger = document.querySelector('[data-mobile-filter-trigger]');
    const drawer = document.querySelector('[data-mobile-filter-drawer]');
    const closeBtn = document.querySelector('[data-mobile-filter-close]');
    const overlay = document.querySelector('[data-mobile-filter-overlay]');

    if (!trigger || !drawer) return;

    const open = () => {
      drawer.classList.remove('hidden');
      document.body.style.overflow = 'hidden';
      trigger.setAttribute('aria-expanded', 'true');
    };

    const close = () => {
      drawer.classList.add('hidden');
      document.body.style.overflow = '';
      trigger.setAttribute('aria-expanded', 'false');
    };

    trigger.onclick = (e) => {
      e.preventDefault();
      open();
    };

    if (closeBtn) closeBtn.onclick = (e) => { e.preventDefault(); close(); };
    if (overlay) overlay.onclick = () => close();

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !drawer.classList.contains('hidden')) {
        close();
      }
    });
  }

  static initFacetSearch() {
    const normalizeText = (str) => {
      return (str || '')
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
    };

    document.querySelectorAll('[data-facet-search]').forEach((input) => {
      if (input.dataset.facetSearchBound === 'true') return;
      input.dataset.facetSearchBound = 'true';

      const group = input.closest('[data-facet-group]');
      const targetList = group?.querySelector('[data-facet-list]');
      const clearBtn = input.parentElement?.querySelector('[data-facet-search-clear]');
      if (!targetList) return;

      let emptyState = targetList.querySelector('[data-facet-empty]');
      if (!emptyState) {
        emptyState = document.createElement('li');
        emptyState.setAttribute('data-facet-empty', '');
        emptyState.className = 'py-4 text-center text-xs text-zinc-400 font-sans italic list-none';
        emptyState.textContent = 'Nenhum resultado encontrado';
        emptyState.style.display = 'none';
        targetList.appendChild(emptyState);
      }

      const updateFilter = () => {
        const rawValue = input.value;
        const normalizedQuery = normalizeText(rawValue);
        const terms = normalizedQuery.split(/\s+/).filter(Boolean);

        if (clearBtn) {
          if (rawValue.length > 0) {
            clearBtn.classList.remove('hidden');
          } else {
            clearBtn.classList.add('hidden');
          }
        }

        const items = targetList.querySelectorAll('li:not([data-facet-empty])');
        let matchCount = 0;

        items.forEach((item) => {
          const labelSpan = item.querySelector('label span') || item.querySelector('label') || item;
          const itemText = normalizeText(labelSpan ? labelSpan.textContent : item.textContent);

          const matches = terms.length === 0 || terms.every((term) => itemText.includes(term));
          if (matches) {
            item.dataset.hidden = 'false';
            item.classList.remove('hidden', 'is-hidden');
            item.style.removeProperty('display');
            matchCount++;
          } else {
            item.dataset.hidden = 'true';
            item.classList.add('hidden', 'is-hidden');
            item.style.setProperty('display', 'none', 'important');
          }
        });

        if (emptyState) {
          const showEmpty = matchCount === 0 && terms.length > 0;
          if (showEmpty) {
            emptyState.dataset.hidden = 'false';
            emptyState.classList.remove('hidden', 'is-hidden');
            emptyState.style.setProperty('display', 'block', 'important');
          } else {
            emptyState.dataset.hidden = 'true';
            emptyState.classList.add('hidden', 'is-hidden');
            emptyState.style.setProperty('display', 'none', 'important');
          }
        }

        if (terms.length > 0) {
          targetList.scrollTop = 0;
        }
      };

      input.addEventListener('input', updateFilter);

      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          input.value = '';
          updateFilter();
          input.blur();
        }
      });

      if (clearBtn) {
        clearBtn.addEventListener('click', (e) => {
          e.preventDefault();
          input.value = '';
          updateFilter();
          input.focus();
        });
      }
    });
  }
}

  window.WilltecCollection = WilltecCollection;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => WilltecCollection.init());
  } else {
    WilltecCollection.init();
  }

  document.addEventListener('shopify:section:load', () => WilltecCollection.init());
})();
